import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { getRoleRepository, useRoleAssignments, ROLE_LABEL, type Role } from "@/lib/admin/role-repository";
import { PageHeader } from "@/components/admin/PageHeader";
import { logAction } from "@/lib/admin/audit/log";

const ROLES: Role[] = ["super_admin", "admin", "manager", "operations", "content_editor", "inventory_manager", "marketing_manager", "support_agent", "read_only"];

export const Route = createFileRoute("/admin/roles")({ component: RolesPage });

function RolesPage() {
  const assignments = useRoleAssignments();
  const [email, setEmail] = useState("");
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState<Role>("admin");

  const grant = async () => {
    const uid = userId.trim();
    if (!uid) return;
    await getRoleRepository().assignRole(uid, role, email.trim() || null);
    logAction({ entity: "role", entityId: uid, action: "grant", summary: `Granted ${ROLE_LABEL[role]} to ${email || uid}` });
    setEmail(""); setUserId("");
  };
  const revoke = async (uid: string, r: Role) => {
    if (!confirm(`Revoke ${ROLE_LABEL[r]} from ${uid}?`)) return;
    await getRoleRepository().removeRole(uid, r);
    logAction({ entity: "role", entityId: uid, action: "revoke", summary: `Revoked ${ROLE_LABEL[r]}` });
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader title="Users & roles" subtitle={`${assignments.length} users with elevated access`}/>

      <div className="border border-black/10 bg-white p-4">
        <h3 className="text-sm font-semibold text-black">Grant role</h3>
        <div className="mt-3 grid gap-3 md:grid-cols-4">
          <label className="grid gap-1"><span className="text-[10px] font-semibold uppercase tracking-widest text-black/60">User ID</span>
            <input value={userId} onChange={(e) => setUserId(e.target.value)} className="border border-black/15 px-2 py-2"/></label>
          <label className="grid gap-1"><span className="text-[10px] font-semibold uppercase tracking-widest text-black/60">Email</span>
            <input value={email} onChange={(e) => setEmail(e.target.value)} className="border border-black/15 px-2 py-2"/></label>
          <label className="grid gap-1"><span className="text-[10px] font-semibold uppercase tracking-widest text-black/60">Role</span>
            <select value={role} onChange={(e) => setRole(e.target.value as Role)} className="border border-black/15 px-2 py-2">
              {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </select></label>
          <div className="flex items-end">
            <button onClick={grant} className="w-full border border-black bg-black px-3 py-2 text-[11px] font-semibold uppercase tracking-widest text-white">Grant access</button>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto border border-black/10 bg-white">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-black/10 text-left text-[10px] font-semibold uppercase tracking-[0.2em] text-black/50">
              <th className="px-3 py-2">User</th>
              <th className="px-3 py-2">Roles</th>
              <th className="px-3 py-2 text-right">Granted</th>
            </tr>
          </thead>
          <tbody>
            {assignments.length === 0
              ? <tr><td colSpan={3} className="px-3 py-6 text-center text-black/40">No role assignments yet.</td></tr>
              : assignments.map((a) => (
                  <tr key={a.userId} className="border-b border-black/5 align-top">
                    <td className="px-3 py-2">
                      <div className="font-medium text-black">{a.email ?? "(no email)"}</div>
                      <div className="text-[11px] font-mono text-black/50">{a.userId}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {a.roles.map((r) => (
                          <span key={r} className="inline-flex items-center gap-1 border border-black/15 px-2 py-0.5 text-[10px] uppercase tracking-widest text-black/70">
                            {ROLE_LABEL[r]}
                            <button onClick={() => revoke(a.userId, r)} className="text-red-700" aria-label={`Revoke ${r}`}>×</button>
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right text-[11px] text-black/50">
                      {a.grantedAt ? new Date(a.grantedAt).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
